import React from 'react';

/* Pro-forma report kit. Shared by StrategyReportSection and
   CPAReportSection so both deliverables read as one document family:
   letter-spaced kickers, hero-stat bands, thin-ruled tables, quiet
   footnotes. */

const MONO = "'IBM Plex Mono', monospace";
const GOLD = '#92700C';

export const kickerStyle = {
  fontFamily: MONO, fontSize: 10, fontWeight: 600, color: GOLD,
  textTransform: 'uppercase', letterSpacing: '0.22em',
};

export function Kicker({ children, color = GOLD, style }) {
  return <div style={{ ...kickerStyle, color, ...style }}>{children}</div>;
}

/* "PRODUCTION · Annual Bitcoin production" + a short intro.
   Sections are labelled by what they cover, not numbered. Ordinals were
   removed deliberately: they added a step of indirection without carrying
   information, and cross-references to them went stale on reorder. */
export function SectionHeader({ kicker, title, intro }) {
  return (
    <div style={{ marginBottom: 16, breakInside: 'avoid', breakAfter: 'avoid' }}>
      <div style={{ borderBottom: '1px solid #E5E5E5', paddingBottom: 10, marginBottom: intro ? 10 : 0 }}>
        {kicker && <Kicker style={{ marginBottom: 3 }}>{kicker}</Kicker>}
        <div style={{ fontSize: 17, fontWeight: 600, color: '#1A1A1A', letterSpacing: '-0.01em' }}>{title}</div>
      </div>
      {intro && <p style={{ fontSize: 11.5, color: '#525252', lineHeight: 1.65, margin: 0, maxWidth: '68ch' }}>{intro}</p>}
    </div>
  );
}

/* Hero-number band: 0.539 / YEAR 1 · 0.381 / YEAR 2 · ... */
export function StatRow({ stats, size = 20, style }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${stats.length}, minmax(0, 1fr))`, gap: 8, margin: '14px 0', ...style }}>
      {stats.map((s, i) => (
        <div key={i} style={{ background: '#FCFCFC', border: '1px solid #F0F0F0', borderTop: `2px solid ${s.accent || '#E5E5E5'}`, borderRadius: 3, padding: '11px 12px', minWidth: 0, overflow: 'hidden' }}>
          <div style={{ fontFamily: MONO, fontSize: size, fontWeight: 600, color: s.color || '#1A1A1A', fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>{s.value}</div>
          <Kicker color="#737373" style={{ marginTop: 5, letterSpacing: '0.12em' }}>{s.label}</Kicker>
          {s.sub && <div style={{ fontSize: 10, color: '#737373', marginTop: 3 }}>{s.sub}</div>}
        </div>
      ))}
    </div>
  );
}

/* Thin-ruled data table. columns: [{key, label, align?, render?}] */
export function ReportTable({ columns, rows, totalRow, footnote }) {
  const th = (c) => ({
    textAlign: c.align || 'left', padding: '7px 10px', borderBottom: '2px solid #1A1A1A',
    fontFamily: MONO, fontSize: 9, fontWeight: 600, color: '#525252',
    textTransform: 'uppercase', letterSpacing: '0.1em', whiteSpace: 'nowrap',
  });
  const td = (c, strong) => ({
    textAlign: c.align || 'left', padding: '7px 10px', borderBottom: '1px solid #F0F0F0',
    fontSize: 11.5, color: strong ? '#1A1A1A' : '#333333', fontWeight: strong ? 600 : 400,
    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
  });
  return (
    <div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>{columns.map(c => <th key={c.key} style={th(c)}>{c.label}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>{columns.map(c => <td key={c.key} style={td(c, r._strong)}>{c.render ? c.render(r) : r[c.key]}</td>)}</tr>
          ))}
          {totalRow && (
            <tr>{columns.map(c => (
              <td key={c.key} style={{ ...td(c, true), borderTop: '2px solid #E5E5E5', borderBottom: 'none' }}>
                {c.render ? c.render(totalRow) : totalRow[c.key]}
              </td>
            ))}</tr>
          )}
        </tbody>
      </table>
      {footnote && <Footnote style={{ marginTop: 8 }}>{footnote}</Footnote>}
    </div>
  );
}

export function Footnote({ children, style }) {
  // Measure matters most here: footnotes carry the disclosures a CPA reads
  // hardest, and unconstrained they ran the full column at roughly 160 characters.
  return <div style={{ fontSize: 9.5, color: '#737373', lineHeight: 1.6, maxWidth: '92ch', ...style }}>{children}</div>;
}

function AdvisorLine({ advisor }) {
  const bits = [advisor?.email, advisor?.phone].filter(Boolean);
  if (!bits.length) return null;
  return <span style={{ fontFamily: MONO, fontSize: 9.5, color: '#737373' }}>{bits.join(' · ')}</span>;
}

/* Cover block: brand, CONFIDENTIAL kicker, title, prepared for/by, date. */
export function ReportCover({ reportKicker, reportTitle, subtitle, preparedFor, advisor, date }) {
  return (
    <div className="print-block print-header" style={{ padding: '36px 40px', borderBottom: '1px solid #E5E5E5', borderLeft: `6px solid ${GOLD}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', color: GOLD }}>
          {advisor?.firm || 'MinerOps'}
        </div>
        <Kicker color="#737373">Confidential · {reportKicker}</Kicker>
      </div>
      <div style={{ marginTop: 26 }}>
        <div style={{ fontSize: 30, fontWeight: 600, color: '#1A1A1A', letterSpacing: '-0.02em', lineHeight: 1.15 }}>{reportTitle}</div>
        {subtitle && <p style={{ fontSize: 12, color: '#525252', lineHeight: 1.6, margin: '8px 0 0', maxWidth: '60ch' }}>{subtitle}</p>}
      </div>
      <div style={{ display: 'flex', gap: 48, marginTop: 26, flexWrap: 'wrap' }}>
        {preparedFor && (
          <div>
            <Kicker color="#737373" style={{ marginBottom: 4 }}>Prepared for</Kicker>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A' }}>{preparedFor}</div>
          </div>
        )}
        {advisor?.name && (
          <div>
            <Kicker color="#737373" style={{ marginBottom: 4 }}>Prepared by</Kicker>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A' }}>{advisor.name}</div>
            {advisor.title && <div style={{ fontSize: 11, color: '#525252' }}>{advisor.title}</div>}
          </div>
        )}
        <div>
          <Kicker color="#737373" style={{ marginBottom: 4 }}>Date</Kicker>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A' }}>{date}</div>
        </div>
      </div>
      <div style={{ marginTop: 18 }}><AdvisorLine advisor={advisor} /></div>
    </div>
  );
}

/* Closing block: "Let's walk through your numbers together." + contact. */
export function NextStep({ advisor, disclaimer }) {
  return (
    <div className="print-block" style={{ marginTop: 8, padding: '26px 28px', background: '#1A1A1A', borderRadius: 4 }}>
      <Kicker color="#B8860B">Next step</Kicker>
      <div style={{ fontSize: 21, fontWeight: 600, color: '#FCFCFC', letterSpacing: '-0.01em', margin: '8px 0 14px' }}>
        Let's walk through your numbers together.
      </div>
      {(advisor?.name || advisor?.email || advisor?.phone) && (
        <div style={{ fontSize: 12, color: '#D4D4D4' }}>
          {advisor.name && <span style={{ fontWeight: 600, color: '#FCFCFC' }}>{advisor.name}</span>}
          {advisor.title && <span> · {advisor.title}</span>}
          <div style={{ fontFamily: MONO, fontSize: 11, color: '#A3A3A3', marginTop: 4 }}>
            {[advisor.email, advisor.phone].filter(Boolean).join(' · ')}
          </div>
        </div>
      )}
      {disclaimer && <div style={{ fontSize: 9, color: '#737373', lineHeight: 1.6, marginTop: 16, borderTop: '1px solid #333333', paddingTop: 10 }}>{disclaimer}</div>}
    </div>
  );
}
